import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Loader2, TrendingUp, TrendingDown, DollarSign, Activity, BarChart3, Globe, Zap, Shield, Brain, PieChart, AlertTriangle, Target, Eye, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import './FullAnalysisModal.css';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { getDashboardMarketData, getDashboardMainContent, getNewsReport, getTechnicalReport, getFundamentalReport, getSentimentReport, getRiskReport, getInvestmentReport, getLastRunDate, hideNativeTabBar, showNativeTabBar } from '../../services/api';
import { AnimatedCircularProgress } from '../Progress/AnimatedCircularProgress';
import { AnimatePresence, motion } from 'framer-motion';

// 유틸리티 함수들
const fmt = {
  num: (val, dec = 2) => {
    if (val == null || isNaN(val)) return '-';
    return Number(val).toFixed(dec);
  },
  pct: (val) => {
    if (val == null || isNaN(val)) return '-';
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  },
  dateTime: (ts) => {
    return new Date(ts).toLocaleString();
  }
};

const trendColor = (val) => {
  if (val == null) return 'text-gray-500';
  if (val > 0) return 'text-green-500 font-medium';
  if (val < 0) return 'text-red-500 font-medium';
  return 'text-gray-500';
};

const chipBg = (level) => {
  if (!level) return 'bg-gray-100 text-gray-700';
  const levelStr = String(level).toUpperCase();
  if (levelStr === 'HIGH' || levelStr === 'INVERTED') return 'bg-red-100 text-red-700';
  if (levelStr === 'LOW' || levelStr === 'NORMAL') return 'bg-green-100 text-green-700';
  if (levelStr === 'STABLE' || levelStr === '0') return 'bg-blue-100 text-blue-700';
  return 'bg-yellow-100 text-yellow-700';
};

const cellColor = (val) => {
  if (val == null) return 'bg-gray-100 text-gray-600';
  if (val > 2) return 'bg-green-500 text-white';
  if (val > 0) return 'bg-green-100 text-green-700';
  if (val < -2) return 'bg-red-500 text-white';
  if (val < 0) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
};

const clsx = (...classes) => classes.filter(Boolean).join(' ');

// XML 전처리 함수 - 닫히지 않은 태그 보정
const preprocessXML = (text) => {
  let processed = text;

  // 1. section 태그 보정
  // <section>이 있는데 </section>이 없으면 다음 <section>, <head> 또는 문자열 끝 전에 추가
  const sectionStarts = [];
  const sectionEnds = [];

  let regex = /<section>/g;
  let match;
  while ((match = regex.exec(processed)) !== null) {
    sectionStarts.push(match.index);
  }

  regex = /<\/section>/g;
  while ((match = regex.exec(processed)) !== null) {
    sectionEnds.push(match.index);
  }

  // section 시작은 있는데 종료가 부족한 경우
  if (sectionStarts.length > sectionEnds.length) {
    const sections = [];
    let lastIndex = 0;

    for (let i = 0; i < sectionStarts.length; i++) {
      const start = sectionStarts[i];
      const nextStart = sectionStarts[i + 1];
      const headMatch = processed.substring(start).search(/<head>/);

      let end;
      if (nextStart !== undefined) {
        end = nextStart;
      } else if (headMatch !== -1) {
        end = start + headMatch;
      } else {
        end = processed.length;
      }

      let sectionContent = processed.substring(start, end);

      // 이미 </section>이 있는지 확인
      if (!sectionContent.includes('</section>')) {
        sectionContent = sectionContent + '</section>\n';
      }

      sections.push(sectionContent);
      lastIndex = end;
    }

    // 재조합
    const beforeFirst = processed.substring(0, sectionStarts[0]);
    const afterLast = processed.substring(lastIndex);
    processed = beforeFirst + sections.join('') + afterLast;
  }

  // 2. content 태그 보정
  // <content>로 시작하는데 </content>가 없으면 다음 태그 전이나 section 종료 전에 추가
  processed = processed.replace(/<content>((?:(?!<\/content>).)*?)(?=<(?:content|subtitle|title|\/section)|$)/gs,
    (match, content) => {
      if (!match.includes('</content>')) {
        return `<content>${content}</content>`;
      }
      return match;
    }
  );

  // 3. ul 태그 보정
  processed = processed.replace(/<ul>((?:(?!<\/ul>).)*?)(?=<(?:\/content|\/section)|$)/gs,
    (match, content) => {
      if (!match.includes('</ul>')) {
        return `<ul>${content}</ul>`;
      }
      return match;
    }
  );

  // 4. list 태그 보정
  processed = processed.replace(/<list>((?:(?!<\/list>).)*?)(?=<(?:\/content|\/section)|$)/gs,
    (match, content) => {
      if (!match.includes('</list>')) {
        return `<list>${content}</list>`;
      }
      return match;
    }
  );

  return processed;
};

// XML 포맷 파싱 함수
const parseXMLFormat = (text) => {
  // 전처리: 닫히지 않은 태그 보정
  const preprocessed = preprocessXML(text);

  console.log('Original text:', text);
  console.log('Preprocessed text:', preprocessed);

  // 정규식으로 태그 추출
  const headRegex = /<head>(.*?)<\/head>/gs;
  const sectionRegex = /<section>(.*?)<\/section>/gs;

  // 전체 텍스트를 순회하며 태그 찾기
  const allMatches = [];

  // head 태그 찾기
  let match;
  while ((match = headRegex.exec(preprocessed)) !== null) {
    allMatches.push({ type: 'head', content: match[1].trim(), index: match.index });
  }

  // section 태그 찾기
  while ((match = sectionRegex.exec(preprocessed)) !== null) {
    allMatches.push({ type: 'section', content: match[1].trim(), index: match.index });
  }

  // 인덱스 순으로 정렬
  allMatches.sort((a, b) => a.index - b.index);

  console.log('Found sections/heads:', allMatches.length);

  // 렌더링
  let sectionCount = 0;
  return allMatches.map((item, idx) => {
    if (item.type === 'head') {
      // 첫 번째 head는 특별한 스타일 (시장 영향 요약 신호등)
      if (idx === 0 && item.content.includes('🚦')) {
        return (
          <div key={`head-${idx}`} className="far-head-banner">
            <span className="far-head-banner-icon">🚦</span>
            <span className="far-head-banner-text">{item.content.replace('🚦', '').trim()}</span>
          </div>
        );
      }

      // 일반 섹션 헤더
      return (
        <div key={`head-${idx}`} className="far-head-divider">
          <div className="far-head-divider-line"></div>
          <span className="far-head-divider-text">{item.content}</span>
          <div className="far-head-divider-line-r"></div>
        </div>
      );
    }

    if (item.type === 'section') {
      return parseSectionContent(item.content, sectionCount++);
    }

    return null;
  });
};

const getSectionVariant = (text) => {
  if (/리스크|위험/i.test(text)) return 'risk';
  if (/기술|technical/i.test(text)) return 'technical';
  if (/펀더멘털|재무/i.test(text)) return 'fundamental';
  if (/감정|sentiment|커뮤니티/i.test(text)) return 'sentiment';
  if (/뉴스|news/i.test(text)) return 'news';
  return 'investment';
};

const parseSectionContent = (content, sectionIndex) => {
  const elements = [];

  // title 추출 (헤더 영역에 별도 렌더링)
  const titleMatch = content.match(/<title>(.*?)<\/title>/s);
  const titleContent = titleMatch ? titleMatch[1].trim() : '';
  const variant = getSectionVariant(titleContent);

  // subtitle 추출
  const subtitleRegex = /<subtitle>(.*?)<\/subtitle>/gs;
  let subtitleMatch;
  let subtitleIdx = 0;
  while ((subtitleMatch = subtitleRegex.exec(content)) !== null) {
    elements.push(
      <div key={`subtitle-${sectionIndex}-${subtitleIdx}`} className="far-subtitle">
        <div className="far-subtitle-bar"></div>
        <span className="far-subtitle-text">{parseInlineFormatting(subtitleMatch[1].trim())}</span>
      </div>
    );
    subtitleIdx++;
  }

  // content 추출 (여러 개 가능)
  const contentRegex = /<content>(.*?)<\/content>/gs;
  let contentMatch;
  let contentIdx = 0;
  while ((contentMatch = contentRegex.exec(content)) !== null) {
    const contentText = contentMatch[1].trim();

    const ulMatch = contentText.match(/<ul>(.*?)<\/ul>/s);
    if (ulMatch) {
      elements.push(parseList(ulMatch[1], 'ul', `${sectionIndex}-${contentIdx}`));
      contentIdx++;
      continue;
    }

    const listMatch = contentText.match(/<list>(.*?)<\/list>/s);
    if (listMatch) {
      elements.push(parseList(listMatch[1], 'list', `${sectionIndex}-${contentIdx}`));
      contentIdx++;
      continue;
    }

    elements.push(
      <div key={`content-${sectionIndex}-${contentIdx}`} className="far-content-text">
        {parseInlineFormatting(contentText)}
      </div>
    );
    contentIdx++;
  }

  return (
    <div key={`section-${sectionIndex}`} className={`far-section-card far-section-card--${variant}`}>
      <div className="far-section-header">
        <div className="far-section-counter">SECTION {String(sectionIndex + 1).padStart(2, '0')}</div>
        {titleContent && (
          <div className="far-section-title">{parseInlineFormatting(titleContent)}</div>
        )}
      </div>
      <div className="far-section-body">{elements}</div>
    </div>
  );
};

const parseList = (content, listType, key) => {
  const liRegex = /<li>(.*?)<\/li>/gs;
  const items = [];
  let match;
  let index = 0;

  while ((match = liRegex.exec(content)) !== null) {
    const itemContent = match[1].trim();

    if (listType === 'ul') {
      items.push(
        <div key={`${key}-li-${index}`} className="far-ul-item">
          <div className="far-ul-dot"></div>
          <div className="far-ul-text">{parseInlineFormatting(itemContent)}</div>
        </div>
      );
    } else {
      items.push(
        <div key={`${key}-li-${index}`} className="far-ol-item">
          <div className="far-ol-num">{index + 1}.</div>
          <div className="far-ol-text">{parseInlineFormatting(itemContent)}</div>
        </div>
      );
    }
    index++;
  }

  return (
    <div key={`list-${key}`} className={listType === 'ul' ? 'far-ul' : 'far-ol'}>
      {items}
    </div>
  );
};

const parseInlineFormatting = (text) => {
  // br 태그를 줄바꿈으로 변환
  let processed = text.replace(/<br\s*\/?>/g, '\n');

  // highlight 태그를 span으로 변환
  processed = processed.replace(
    /<highlight>(.*?)<\/highlight>/g,
    (match, content) => {
      if (content.includes('🟢')) {
        return `<span class="far-highlight far-highlight--green">${content}</span>`;
      } else if (content.includes('🟡')) {
        return `<span class="far-highlight far-highlight--yellow">${content}</span>`;
      } else if (content.includes('🔴')) {
        return `<span class="far-highlight far-highlight--red">${content}</span>`;
      }
      return `<span class="far-highlight far-highlight--data">${content}</span>`;
    }
  );

  // p 태그나 다른 태그 제거 (content로 취급)
  processed = processed.replace(/<\/?p>/g, '');

  return <span dangerouslySetInnerHTML={{ __html: processed }} />;
};

// 텍스트 리포트 파서 함수
const parseTextReport = (text) => {
  if (!text) return null;

  // 박스 경계선 및 구분선 제거
  const cleaned = text
    .replace(/┌─+┐|└─+┘|│/g, '')
    .replace(/═+/g, '')
    .trim();

  const sections = [];
  const lines = cleaned.split('\n');
  let currentSection = null;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    // 메인 타이틀 감지 (💰 쉬운 주식시장 분석 리포트, 🌍 고급 시장 인텔리전스 추가 분석)
    if (line.includes('💰 쉬운') || line.includes('🌍 고급')) {
      if (currentSection) sections.push(currentSection);
      currentSection = {
        type: 'main_title',
        title: line,
        content: []
      };
      continue;
    }

    // 섹션 헤더 감지 - 이모지로 시작하는 라인
    // 🎯, 📊, 😨, 💵, 📈, 🏢, 🛢️, ₿, 🤔, 🇯🇵, 📅, 💡 등
    const emojiPattern = /^([\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+)\s*(.+)$/u;
    const sectionMatch = line.match(emojiPattern);

    if (sectionMatch && !line.includes(':')) {
      // 섹션 헤더로 인식 (콜론이 없는 경우만)
      if (currentSection) sections.push(currentSection);
      currentSection = {
        type: 'section',
        icon: sectionMatch[1],
        title: sectionMatch[2].replace(/【|】/g, '').trim(),
        content: []
      };
      continue;
    }

    // 데이터 항목 수집
    if (currentSection) {
      currentSection.content.push(line);
    }
  }

  if (currentSection) sections.push(currentSection);

  return sections;
};

// 텍스트 리포트 렌더링 컴포넌트
const TextReportSection = ({ section, index }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  if (section.type === 'main_title') {
    return (
      <div key={index} className="far-text-main-title">
        <Brain size={22} className="flex-shrink-0" />
        <span className="far-text-main-title-text">{section.title}</span>
      </div>
    );
  }

  if (section.type === 'section') {
    // 투자 위험도 추출
    const riskMatch = section.content.join('\n').match(/투자 위험도:\s*(.+?)\s*([📗📙📕])/);
    const summaryMatch = section.content.join('\n').match(/간단 요약:\s*(.+?)\s*\((.+?)\)/);

    // VIX 지수 추출
    const vixMatch = section.content.join('\n').match(/공포지수\(VIX\):\s*([\d.]+)/);

    // 금리 데이터 추출
    const yield2yMatch = section.content.join('\n').match(/2년 금리:\s*([\d.]+)%/);
    const yield10yMatch = section.content.join('\n').match(/10년 금리:\s*([\d.]+)%/);
    const yieldSpreadMatch = section.content.join('\n').match(/장기금리가 단기금리보다\s*([\d.]+)%p\s*(높음|낮음)/);

    // 달러/금 데이터 추출
    const dxyMatch = section.content.join('\n').match(/달러 지수:\s*([\d.]+)\s*\(([+-][\d.]+)%\)/);
    const goldMatch = section.content.join('\n').match(/금 가격:\s*\$([,\d]+)\s*\(([+-][\d.]+)%\)/);

    // 주식 시장 데이터 추출
    const sp500Match = section.content.join('\n').match(/S&P 500.*?:\s*([,\d]+)\s*\(([+-][\d.]+)%\)/);
    const russell2kMatch = section.content.join('\n').match(/러셀 2000.*?:\s*([,\d]+)\s*\(([+-][\d.]+)%\)/);

    // 업종 데이터 추출
    const bankMatch = section.content.join('\n').match(/은행\/금융주:\s*([+-][\d.]+)%/);
    const techMatch = section.content.join('\n').match(/기술주:\s*([+-][\d.]+)%/);

    // 원자재 데이터 추출
    const wtiMatch = section.content.join('\n').match(/석유\(WTI\):\s*\$?([\d.]+)\s*\(([+-][\d.]+)%\)/);
    const copperMatch = section.content.join('\n').match(/구리:\s*\$?([\d.]+)\s*\(([+-][\d.]+)%\)/);

    // 비트코인 데이터 추출
    const btcMatch = section.content.join('\n').match(/비트코인:\s*\$([,\d]+)\s*\(([+-][\d.]+)%\)/);

    return (
      <div key={index} className="far-text-section">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="far-text-section-toggle"
        >
          <div className="far-text-section-icon-wrap">
            <span className="far-text-section-icon">{section.icon}</span>
            <span className="far-text-section-title">{section.title}</span>
          </div>
          <span className="far-text-section-chevron">
            {isExpanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
          </span>
        </button>

        {isExpanded && (
          <div className="far-text-section-body">
            {/* 투자 위험도 카드 */}
            {riskMatch && (
              <div className={`p-4 rounded-xl border-2 ${
                riskMatch[2] === '📗' ? 'bg-green-50 border-green-300' :
                riskMatch[2] === '📙' ? 'bg-yellow-50 border-yellow-300' :
                'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Target size={18} className={
                    riskMatch[2] === '📗' ? 'text-green-600' :
                    riskMatch[2] === '📙' ? 'text-yellow-600' :
                    'text-red-600'
                  } />
                  <span className="font-bold text-gray-900">투자 위험도</span>
                </div>
                <div className="text-xl font-bold mb-1">
                  {riskMatch[1]} {riskMatch[2]}
                </div>
              </div>
            )}

            {/* 간단 요약 카드 */}
            {summaryMatch && (
              <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Zap size={20} className="text-blue-600" />
                  <span className="font-bold text-gray-900">간단 요약</span>
                </div>
                <div className="text-lg font-semibold text-blue-900">
                  ✅ {summaryMatch[1]} ({summaryMatch[2]})
                </div>
              </div>
            )}

            {/* VIX 공포지수 카드 */}
            {vixMatch && (
              <div className={`p-4 rounded-xl border-2 ${
                parseFloat(vixMatch[1]) > 30 ? 'bg-red-50 border-red-300' :
                parseFloat(vixMatch[1]) > 20 ? 'bg-yellow-50 border-yellow-300' :
                'bg-green-50 border-green-300'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={18} className={
                    parseFloat(vixMatch[1]) > 30 ? 'text-red-600' :
                    parseFloat(vixMatch[1]) > 20 ? 'text-yellow-600' :
                    'text-green-600'
                  } />
                  <span className="font-bold text-gray-900">VIX 공포지수</span>
                </div>
                <div className="text-2xl font-bold">
                  {vixMatch[1]}
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  {parseFloat(vixMatch[1]) > 30 ? '😱 시장이 매우 불안정해요' :
                   parseFloat(vixMatch[1]) > 20 ? '😐 시장이 다소 불안해요' :
                   '😊 시장이 평온해요'}
                </div>
              </div>
            )}

            {/* 금리 정보 카드 */}
            {(yield2yMatch || yield10yMatch) && (
              <div className="p-4 rounded-xl bg-indigo-50 border-2 border-indigo-200">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 size={18} className="text-indigo-600" />
                  <span className="font-bold text-gray-900">금리 상황</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {yield2yMatch && (
                    <div className="bg-white p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">2년 금리</div>
                      <div className="text-xl font-bold text-indigo-700">{yield2yMatch[1]}%</div>
                    </div>
                  )}
                  {yield10yMatch && (
                    <div className="bg-white p-3 rounded-lg">
                      <div className="text-xs text-gray-600 mb-1">10년 금리</div>
                      <div className="text-xl font-bold text-indigo-700">{yield10yMatch[1]}%</div>
                    </div>
                  )}
                </div>
                {yieldSpreadMatch && (
                  <div className="mt-3 p-3 bg-white rounded-lg">
                    <div className="text-xs font-medium text-gray-700">
                      장기금리가 단기금리보다 <span className="font-bold text-indigo-600">{yieldSpreadMatch[1]}%p {yieldSpreadMatch[2]}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 달러/금 카드 */}
            {(dxyMatch || goldMatch) && (
              <div className="grid grid-cols-2 gap-3">
                {dxyMatch && (
                  <div className="p-4 rounded-xl bg-green-50 border-2 border-green-200">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign size={16} className="text-green-600" />
                      <span className="text-sm font-bold text-gray-900">달러 지수</span>
                    </div>
                    <div className="text-xl font-bold text-green-700">{dxyMatch[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(dxyMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {dxyMatch[2]}%
                    </div>
                  </div>
                )}
                {goldMatch && (
                  <div className="p-4 rounded-xl bg-yellow-50 border-2 border-yellow-300">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base">💰</span>
                      <span className="text-sm font-bold text-gray-900">금 가격</span>
                    </div>
                    <div className="text-xl font-bold text-yellow-700">${goldMatch[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(goldMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {goldMatch[2]}%
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 주식 시장 카드 */}
            {(sp500Match || russell2kMatch) && (
              <div className="grid grid-cols-2 gap-3">
                {sp500Match && (
                  <div className="p-4 rounded-xl bg-blue-50 border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={16} className="text-blue-600" />
                      <span className="text-sm font-bold text-gray-900">S&P 500</span>
                    </div>
                    <div className="text-xl font-bold text-blue-700">{sp500Match[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(sp500Match[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {sp500Match[2]}%
                    </div>
                  </div>
                )}
                {russell2kMatch && (
                  <div className="p-4 rounded-xl bg-purple-50 border-2 border-purple-200">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp size={16} className="text-purple-600" />
                      <span className="text-sm font-bold text-gray-900">러셀 2000</span>
                    </div>
                    <div className="text-xl font-bold text-purple-700">{russell2kMatch[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(russell2kMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {russell2kMatch[2]}%
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 업종 카드 */}
            {(bankMatch || techMatch) && (
              <div className="grid grid-cols-2 gap-3">
                {bankMatch && (
                  <div className="p-4 rounded-xl bg-teal-50 border-2 border-teal-200">
                    <div className="text-sm font-bold text-gray-900 mb-2">은행/금융주</div>
                    <div className={`text-xl font-bold ${parseFloat(bankMatch[1]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {bankMatch[1]}%
                    </div>
                  </div>
                )}
                {techMatch && (
                  <div className="p-4 rounded-xl bg-cyan-50 border-2 border-cyan-200">
                    <div className="text-sm font-bold text-gray-900 mb-2">기술주</div>
                    <div className={`text-xl font-bold ${parseFloat(techMatch[1]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {techMatch[1]}%
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 원자재 카드 */}
            {(wtiMatch || copperMatch) && (
              <div className="grid grid-cols-2 gap-3">
                {wtiMatch && (
                  <div className="p-4 rounded-xl bg-gray-100 border-2 border-gray-300">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base">🛢️</span>
                      <span className="text-sm font-bold text-gray-900">석유 (WTI)</span>
                    </div>
                    <div className="text-xl font-bold text-gray-800">${wtiMatch[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(wtiMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {wtiMatch[2]}%
                    </div>
                  </div>
                )}
                {copperMatch && (
                  <div className="p-4 rounded-xl bg-orange-50 border-2 border-orange-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-base">🔨</span>
                      <span className="text-sm font-bold text-gray-900">구리</span>
                    </div>
                    <div className="text-xl font-bold text-orange-700">${copperMatch[1]}</div>
                    <div className={`text-xs font-medium ${parseFloat(copperMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {copperMatch[2]}%
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* 비트코인 카드 */}
            {btcMatch && (
              <div className="p-4 rounded-xl bg-amber-50 border-2 border-amber-300">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-base">₿</span>
                  <span className="font-bold text-gray-900">비트코인</span>
                </div>
                <div className="text-xl font-bold text-amber-700">${btcMatch[1]}</div>
                <div className={`text-xs font-medium ${parseFloat(btcMatch[2]) > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {btcMatch[2]}%
                </div>
              </div>
            )}

            {/* 나머지 텍스트 내용 */}
            <div className="space-y-3">
              {section.content.map((line, idx) => {
                const trimmed = line.trim();
                if (!trimmed) return null;

                // 이미 파싱된 데이터는 건너뛰기
                if (
                  trimmed.includes('투자 위험도:') ||
                  trimmed.includes('간단 요약:') ||
                  trimmed.includes('공포지수(VIX):') ||
                  trimmed.includes('금 가격:') ||
                  trimmed.includes('달러 지수:') ||
                  trimmed.includes('S&P 500') ||
                  trimmed.includes('러셀 2000') ||
                  trimmed.includes('은행/금융주:') ||
                  trimmed.includes('기술주:') ||
                  trimmed.includes('석유(WTI):') ||
                  trimmed.includes('구리:') ||
                  trimmed.includes('비트코인:') ||
                  trimmed.includes('2년 금리:') ||
                  trimmed.includes('10년 금리:') ||
                  trimmed.includes('장기금리가 단기금리보다')
                ) {
                  return null;
                }

                // 서브 헤더 감지 (이모지로 시작하고 짧은 텍스트)
                const subHeaderMatch = trimmed.match(/^([\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+)\s*(.+)$/u);
                if (subHeaderMatch && trimmed.length < 50 && !trimmed.includes(':')) {
                  return (
                    <div key={idx} className="far-text-subhead">
                      <span className="far-text-subhead-icon">{subHeaderMatch[1]}</span>
                      <span className="far-text-subhead-text">{subHeaderMatch[2]}</span>
                    </div>
                  );
                }

                // 서브 타이틀 (이모지 없이 콜론으로 끝나는 경우)
                if (trimmed.endsWith(':') && trimmed.length < 50) {
                  return (
                    <div key={idx} className="far-text-subtitle">{trimmed}</div>
                  );
                }

                // 리스트 항목
                if (trimmed.startsWith('•') || trimmed.startsWith('-') || trimmed.startsWith('*')) {
                  return (
                    <div key={idx} className="far-text-bullet">
                      <div className="far-text-bullet-dot"></div>
                      <div className="far-text-bullet-text">{trimmed.replace(/^[•\-*]\s*/, '')}</div>
                    </div>
                  );
                }

                // 긍정/부정 표현 강조
                if (
                  trimmed.includes('정상적으로') ||
                  trimmed.includes('안정적') ||
                  trimmed.includes('좋아요') ||
                  trimmed.includes('평온해요')
                ) {
                  return (
                    <div key={idx} className="far-text-positive">
                      <span>👍</span>
                      <span>{trimmed}</span>
                    </div>
                  );
                }

                if (
                  trimmed.includes('불안정') ||
                  trimmed.includes('위험') ||
                  trimmed.includes('조심')
                ) {
                  return (
                    <div key={idx} className="far-text-warning">
                      <span>⚠️</span>
                      <span>{trimmed}</span>
                    </div>
                  );
                }

                // 일반 텍스트
                return (
                  <div key={idx} className="far-text-line">{trimmed}</div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
};

// Dashboard UI Components
function KPI({ label, value, changePct, suffix = '' }) {
  const val = typeof value === 'number' ? fmt.num(value) : (value ?? '-');
  
  return (
    <div className="p-3 rounded-lg border bg-white shadow-sm">
      <div className="text-gray-500 text-sm mb-2">{label}</div>
      <div className="flex items-center justify-between">
        <div className="text-lg font-semibold">{val}{typeof value === 'number' ? suffix : ''}</div>
        {typeof changePct === 'number' && (
          <div className={`text-sm font-medium ${trendColor(changePct)}`}>
            {fmt.pct(changePct)}
          </div>
        )}
      </div>
    </div>
  );
}

function KPIGrid({ data }) {
  const c = data.changes || {};
  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      <KPI label="S&P 500" value={data.sp500_price || data.spx} changePct={c.spx} />
      <KPI label="Russell 2000" value={data.russell2k} changePct={c.russell2k} />
      <KPI label="기술주 (XLK)" value={data.technology_xlk} changePct={c.technology} />
      <KPI label="금융주 (XLF)" value={data.financials_xlf} changePct={c.financials} />
      <KPI label="VIX 공포지수" value={data.vix} changePct={c.vix} />
      <KPI label="달러 지수" value={data.dxy} changePct={c.dxy} />
      <KPI label="금 ($/oz)" value={data.gold_price} changePct={c.gold} />
      <KPI label="WTI 원유" value={data.wti_price} changePct={c.wti_oil} />
      <KPI label="구리 ($/lb)" value={data.copper_price} changePct={c.copper} />
      <KPI label="비트코인" value={data.bitcoin_price} changePct={c.bitcoin} />
      <KPI label="USD/JPY" value={data.usdjpy} changePct={c.usdjpy} />
      <KPI label="10년 국채" value={data.us_10y_yield} changePct={c.us_10y} suffix="%" />
    </div>
  );
}

function YieldCurveCard({ data }) {
  const chartData = [
    { tenor: '2Y', yield: data.us_2y_yield },
    { tenor: '10Y', yield: data.us_10y_yield },
    { tenor: '30Y', yield: data.us_30y_yield },
  ].filter(item => item.yield != null);
  
  const inverted = (data.yield_2y10y_spread != null && data.yield_2y10y_spread < 0) || 
                   (data.yield_10y30y_spread != null && data.yield_10y30y_spread < 0);
  
  if (chartData.length === 0) return null;
  
  return (
    <div className="p-4 rounded-lg border bg-white shadow-sm">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold">미국 수익률 곡선</div>
        <div className={clsx('px-2 py-1 rounded text-xs', chipBg(inverted ? 'HIGH' : data.curve_steepness || 'NORMAL'))}>
          {inverted ? '역전' : data.curve_steepness || 'NORMAL'}
        </div>
      </div>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ left: 10, right: 10, top: 10, bottom: 0 }}>
            <XAxis dataKey="tenor" />
            <YAxis domain={['auto', 'auto']} tickFormatter={(v) => `${Number(v).toFixed(2)}%`} />
            <Tooltip formatter={(v) => [`${Number(v).toFixed(3)}%`, 'Yield']} />
            <ReferenceLine y={0} stroke="#e5e7eb" />
            <Line type="monotone" dataKey="yield" stroke={inverted ? '#ef4444' : '#3b82f6'} strokeWidth={2} dot />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Badge({ label, level }) {
  return (
    <span className={clsx('px-2 py-1 rounded text-xs font-medium', chipBg(level))}>
      {label}: {level ?? '-'}
    </span>
  );
}

function RiskBadges({ data }) {
  return (
    <div className="flex flex-wrap gap-2 mb-4">
      <Badge label="VIX" level={data.vix_regime} />
      <Badge label="수익률 곡선" level={data.curve_steepness} />
      <Badge label="감마 스퀴즈" level={data.gamma_squeeze_risk} />
      <Badge label="엔 캐리" level={data.yen_carry_metrics?.risk_level} />
      <Badge label="지정학적 리스크" level={String(data.geopolitical_risk_score)} />
      {data.triple_witching_proximity != null && (
        <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-700">
          Triple Witching D-{data.triple_witching_proximity}
        </span>
      )}
    </div>
  );
}

function ChangesHeatmap({ data }) {
  const keys = [
    ['spx','technology','financials','russell2k','utilities'],
    ['vix','dxy','gold','copper','wti_oil','bitcoin','usdjpy'],
    ['defense_etf','china_etf','emerging_markets']
  ];
  const c = data.changes || {};
  
  return (
    <div className="p-4 rounded-lg border bg-white shadow-sm">
      <div className="font-semibold mb-3">일일 변화율 히트맵</div>
      <div className="space-y-2">
        {keys.map((row, i) => (
          <div key={i} className="flex gap-2 flex-wrap">
            {row.map(k => (
              <div key={k} className={clsx('px-2 py-1 rounded text-xs font-medium', cellColor(c[k]))}>
                {k.toUpperCase().replace(/_/g, ' ')}: {c[k] != null ? `${c[k].toFixed(2)}%` : '-'}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export const FullAnalysisModal = ({ isOpen, onClose, stockSymbol = "STOCK", analysisText = "", reportType = "investment" }) => {
  const [copied, setCopied] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardMainContent, setDashboardMainContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (isOpen) hideNativeTabBar();
    else showNativeTabBar();
    return () => showNativeTabBar();
  }, [isOpen]);

  // Mobile detection for market mode
  useEffect(() => {
    if (reportType === "market") {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }
  }, [reportType]);

  // Fetch report when modal opens
  useEffect(() => {
    if (isOpen && stockSymbol && stockSymbol !== "STOCK" && reportType !== "market") {
      fetchReport();
    } else if (isOpen && reportType === "market") {
      fetchDashboardData();
      fetchDashboardMainContent();
    }
  }, [isOpen, stockSymbol, reportType]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get last run date for this ticker
      const runDate = await getLastRunDate(stockSymbol);
      let data;

      if (reportType === "news") {
        data = await getNewsReport(stockSymbol, runDate);
      } else if (reportType === "technical") {
        data = await getTechnicalReport(stockSymbol, runDate);
      } else if (reportType === "fundamental") {
        data = await getFundamentalReport(stockSymbol, runDate);
      } else if (reportType === "sentiment") {
        data = await getSentimentReport(stockSymbol, runDate);
      } else if (reportType === "risk") {
        data = await getRiskReport(stockSymbol, runDate);
      } else {
        data = await getInvestmentReport(stockSymbol, runDate);
      }

      setReportData(data);
    } catch (err) {
      const reportTypeName = reportType === "news" ? "뉴스 분석" : reportType === "technical" ? "기술적 분석" : reportType === "fundamental" ? "펀더멘털 분석" : reportType === "sentiment" ? "커뮤니티 분석" : reportType === "risk" ? "리스크 분석" : "투자 분석";
      setError(`${reportTypeName} 보고서를 불러오는 중 오류가 발생했습니다.`);
      console.error(`Failed to fetch ${reportType} report:`, err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      // const data = await getDashboardMainContent();
      const data = await getDashboardMarketData(); // Use fixed date for now
      setDashboardData(data);
    } catch (err) {
      setError('시장 분석 데이터를 불러오는 중 오류가 발생했습니다.');
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardMainContent = async () => {
    try {
      setLoading(true);
      setError(null);
      // const data = await getDashboardMainContent();
      const data = await getDashboardMainContent(); // Use fixed date for now
      setDashboardMainContent(data);
    } catch (err) {
      setError('시장 분석 데이터를 불러오는 중 오류가 발생했습니다.');
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }

  // Market mode sections
  const marketSections = [
    { id: 'overview', name: '리포트', icon: <Brain size={16} /> },
  ];

  const handleCopy = async () => {
    try {
      const textToCopy = reportType === "market" ? 
        (dashboardData ? JSON.stringify(dashboardData, null, 2) : '') : 
        (reportData?.data || analysisText);
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('복사 실패:', err);
    }
  };

  // Modern Market Analysis Render Function
  const renderMarketContent = () => {
    if (reportType !== "market") return null;

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-gray-600">시장 데이터를 불러오는 중...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-red-600 font-medium mb-2">{error}</div>
            <button 
              onClick={fetchDashboardData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              다시 시도
            </button>
          </div>
        </div>
      );
    }

    if (!dashboardData) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="text-gray-600">시장 분석 데이터가 없습니다.</div>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* 헤더 */}
            <div className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-6 md:p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-10 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-white opacity-10 rounded-full -ml-16 -mb-16"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                    <Brain size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl md:text-3xl font-bold">AI 시장분석 리포트</h3>
                    <p className="text-white text-opacity-90 text-sm mt-1">실시간 글로벌 시장 인사이트</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AI 핵심 요약 카드 */}
            <div className="bg-white rounded-2xl p-6 border-2 border-blue-100 shadow-lg relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -mr-16 -mt-16"></div>
              <div className="relative z-10">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap size={20} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                      💡 AI 핵심 요약
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">한눈에 보는 오늘의 시장</p>
                  </div>
                </div>
                <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                  {dashboardMainContent?.aiSummary || '시장 분석 데이터를 불러오는 중입니다...'}
                </p>
              </div>
            </div>

            {/* 상세 분석 내용 */}
            <div className="space-y-4">
              {dashboardMainContent?.mainContent ? (
                <>
                  {/* XML 형식 파싱 */}
                  {parseXMLFormat(dashboardMainContent.mainContent)}
                </>
              ) : (
                <div className="bg-white rounded-xl p-6 border border-gray-200">
                  <div className="text-sm text-gray-500 text-center">시장 분석 데이터를 불러오는 중입니다...</div>
                </div>
              )}
            </div>

            {/* 미국 국채 곡선 */}
            {dashboardData && (
              <div className="bg-white rounded-2xl p-6 border-2 border-indigo-100 shadow-lg">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center">
                    <BarChart3 size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900">📈 미국 국채 곡선</h4>
                    <p className="text-xs text-gray-500">국채 금리 동향 분석</p>
                  </div>
                </div>
                <YieldCurveCard data={dashboardData} />
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
          />
          <motion.div
            className="far-modal-root fixed inset-0 z-[10000] bg-white flex flex-col overflow-hidden"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            onClick={e => e.stopPropagation()}
          >
        {/* 헤더 */}
        <div className="far-header">
          <div className="far-header-left">
            <span className="far-header-report-type">
              {reportType === "market" ? "MARKET ANALYSIS" :
               reportType === "news" ? "NEWS ANALYSIS" :
               reportType === "technical" ? "TECHNICAL ANALYSIS" :
               reportType === "fundamental" ? "FUNDAMENTAL ANALYSIS" :
               reportType === "sentiment" ? "SENTIMENT ANALYSIS" :
               reportType === "risk" ? "RISK ANALYSIS" :
               "INVESTMENT ANALYSIS"}
            </span>
            <div className="far-header-title-row">
              <span className="far-header-title">
                투자특공대
              </span>
              {reportType !== "market" && (
                <span className="far-ticker-chip">{stockSymbol}</span>
              )}
              <span className="far-header-title">
                {reportType === "market" ? "시장 종합 분석 보고서" :
                 reportType === "news" ? "뉴스 분석 보고서" :
                 reportType === "technical" ? "기술적 분석 보고서" :
                 reportType === "fundamental" ? "펀더멘털 분석 보고서" :
                 reportType === "sentiment" ? "커뮤니티 분석 보고서" :
                 reportType === "risk" ? "리스크 분석 보고서" :
                 "투자 분석 보고서"}
              </span>
            </div>
          </div>
          <button onClick={onClose} className="far-close-btn">
            <X size={15} />
          </button>
        </div>

        {/* 컨텐츠 */}
        {reportType === "market" ? (
          // Modern Market Analysis with Tabs
          isMobile ? (
            <>
              <div className="bg-white border-b border-gray-200 flex-shrink-0">
                <div className="flex overflow-x-auto scrollbar-hide p-2 space-x-1">
                  {marketSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`flex items-center space-x-1 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {section.icon}
                      <span>{section.name}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 pb-safe">
                {renderMarketContent()}
              </div>
            </>
          ) : (
            <div className="flex flex-1">
              <div className="w-56 bg-gray-50 border-r border-gray-200 p-4 overflow-y-auto">
                <nav className="space-y-2">
                  {marketSections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {section.icon}
                      <span className="text-sm font-medium">{section.name}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {renderMarketContent()}
              </div>
            </div>
          )
        ) : (
          <div className="far-content-area">
            <div className="far-sections-stack">
              {reportType === "market" ? (
              // 시장 분석 - 새로운 카드 UI
              loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <span className="ml-3 text-gray-600">시장 데이터를 불러오는 중...</span>
                </div>
              ) : error ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="text-red-500 font-medium mb-2">{error}</div>
                    <button 
                      onClick={fetchDashboardData}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      다시 시도
                    </button>
                  </div>
                </div>
              ) : dashboardData ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="text-gray-600">데이터 표시 방법을 선택하세요.</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="text-gray-600">시장 분석 데이터가 없습니다.</div>
                  </div>
                </div>
              )
            ) : (
              // 기존 텍스트 보고서 UI
              // <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
                <div className="p-1 md:p-2">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <span className="ml-3 text-gray-500">투자 분석 보고서를 불러오는 중...</span>
                    </div>
                  ) : error ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="text-center">
                        <div className="text-red-500 font-medium mb-2">{error}</div>
                        <button 
                          onClick={fetchReport}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          다시 시도
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {(() => {
                        const rawText = reportData?.data || analysisText;

                        // XML 형식 감지
                        if (rawText.includes('<head>') || rawText.includes('<section>')) {
                          return parseXMLFormat(rawText);
                        }

                        // 기존 마크다운 형식
                        return rawText.replace(/---/g, '').split('\n\n').map((paragraph, index) => {
                          const trimmedParagraph = paragraph.trim();

                          // 메인 섹션 헤더 감지
                          const isHeader = (
                            (trimmedParagraph.match(/^###\s*\d+\./) && trimmedParagraph.length < 100) ||
                            (trimmedParagraph.match(/^\*\*\d+\.\s*.+\*\*$/) && trimmedParagraph.length < 100)
                          );

                          if (isHeader) {
                            let cleanTitle = trimmedParagraph
                              .replace(/^---\s*/, '')
                              .replace(/^\*\*|\*\*$/g, '')
                              .replace(/^###\s*/, '')
                              .trim();

                            return (
                              <div key={index} className="far-md-header">{cleanTitle}</div>
                            );
                          }

                          // 강조된 텍스트
                          if (trimmedParagraph.includes('**') && !trimmedParagraph.match(/^\*\*\d+\./)) {
                            const formatted = trimmedParagraph.replace(
                              /\*\*(.*?)\*\*/g,
                              '<span class="far-highlight far-highlight--data">$1</span>'
                            );
                            return (
                              <div
                                key={index}
                                className="far-text-line"
                                dangerouslySetInnerHTML={{ __html: formatted }}
                              />
                            );
                          }

                          // 리스트 항목
                          if (trimmedParagraph.startsWith('* ') || trimmedParagraph.startsWith('- ')) {
                            return (
                              <div key={index} className="far-md-bullet">
                                <div className="far-md-bullet-dot"></div>
                                <div className="far-md-bullet-text">{trimmedParagraph.replace(/^[\*\-]\s*/, '')}</div>
                              </div>
                            );
                          }

                          // 일반 텍스트
                          return (
                            <div key={index} className="far-text-line">{trimmedParagraph}</div>
                          );
                        });
                      })()}
                    </div>
                  )}
                </div>
              // </div>
            )}
            </div>
          </div>
        )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FullAnalysisModal;